/**
 * Switching between an insurer's lines of business without signing out.
 *
 * Ergo Baltic sells motor and property. They are separate deployments — their
 * own hosts, source names, media servers and case-detail pages — so each is its
 * own client here, and the two are tied together by `lineGroup`. Choosing a
 * line is choosing which of them the session points at.
 *
 * Who may: only an access level that is not itself line-scoped.
 *
 * Ergo's levels answer this on their own. 11-22 name both a country AND a line
 * — EE Motor Sales, LV Property UW — so a motor underwriter has no business in
 * property, and offering the switch would be offering to leave their remit. 10
 * (Admin) is the only level that names neither, so it is the only one for which
 * a line switch is meaningful.
 */

/**
 * Levels that are not tied to a line. 0 is included because some deployments
 * use it for a superuser; it costs nothing, since the group check below already
 * limits this to Ergo.
 */
export const LINE_ADMIN_LEVELS = [0, 10];

export const isLineAdmin = (profile) => {
  const level = Number.parseInt(profile?.access_level, 10);
  return Number.isFinite(level) && LINE_ADMIN_LEVELS.includes(level);
};

/**
 * The lines available from the current session, current one included, or [] if
 * there is no choice to offer.
 *
 * Empty for a client with no sibling — Bajaj and Hegic sell one line — and
 * empty for a user whose level is line-scoped, so the caller renders nothing
 * rather than a control with one option.
 */
/**
 * The registry entry for a name, whichever of its two names this is.
 *
 * A client answers to its registry key ("ergo_property") and to its own
 * sourceName ("ergobalticproperty"), and which one is in localStorage depends
 * on who wrote it last: the line switcher writes the key, Layout writes the
 * source name when it normalises a property path. A strict clients[key] lookup
 * therefore found nothing after a hard refresh inside a property case, so this
 * returned [] and the Motor/Property control silently disappeared.
 */
const entryFor = (name, clients) => {
  if (clients[name]) return [name, clients[name]];
  return (
    Object.entries(clients).find(([, c]) => c?.sourceName === name) || [null, null]
  );
};

export const lineOptions = (currentName, clients = {}, profile = null) => {
  const [currentKey, current] = entryFor(currentName, clients);
  if (!current?.lineGroup || !isLineAdmin(profile)) return [];

  const siblings = Object.entries(clients).filter(
    ([, client]) => client?.lineGroup === current.lineGroup,
  );
  if (siblings.length < 2) return [];

  return siblings
    .map(([key, client]) => ({
      key,
      line: client.inspectionLine || null,
      label: client.inspectionLine
        ? client.inspectionLine.charAt(0).toUpperCase() +
          client.inspectionLine.slice(1)
        : client.label || key,
      current: key === currentKey,
    }))
    // Motor before property: the order they are sold in, and stable so the
    // control does not reorder itself between renders.
    .sort((a, b) => String(a.line).localeCompare(String(b.line)));
};
